import React from 'react';

import { RadioButton, TextInput } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import { getSettingsLabels } from 'client/blocks/Settings/channels/labels';
import { Field } from 'client/blocks-redesign/form/Field/Field';
import { Link } from 'client/blocks-redesign/Link/Link';
import { FormErrors } from 'client/hocs/types';
import { wrapInText } from 'client/lib/i18n-utils';
import { routes } from 'client/lib/routes';
import { AliceSkill, Channel, SmartHome } from 'client/model/skill';
import { AliceSkillFormSettings, BackendType, SmartHomeFormSettings } from 'client/types';
import { getIsYangoRequest } from 'client/utils';
import * as keyset from 'i18n/SmartHomeForm';

import styles from './BackendTypeField.module.scss';
import FunctionsContainer from './FunctionsContainer';

const t = i18n(keyset);

interface Props<T extends AliceSkill | SmartHome> {
    onWebhookChange: (value: string) => void;
    onFunctionIdChange: (value: string) => void;
    onBackendTypeChange: (value: BackendType) => void;
    disabled?: boolean;
    errors: T extends AliceSkill ? FormErrors<AliceSkillFormSettings> : FormErrors<SmartHomeFormSettings>;
    values: T extends AliceSkill ? AliceSkillFormSettings : SmartHomeFormSettings;
    skill: T;
}

export function BackendTypeField<T extends AliceSkill | SmartHome>({
    onWebhookChange,
    onFunctionIdChange,
    onBackendTypeChange,
    disabled,
    errors,
    values,
    skill,
}: Props<T>) {
    const l = getSettingsLabels()[skill.channel];

    const aliceSkillWebhookDescription = wrapInText((s) => (
        <Link theme="info" external target="_blank" to={routes.documentation.aliceSkills.webhookUrl}>
            {s}
        </Link>
    ))(t('backend-caption-with-doc-link'));

    const smartHomeWebhookDescription = wrapInText((s) => (
        <Link theme="info" external target="_blank" to={routes.documentation.smartHome.webhookUrl}>
            {s}
        </Link>
    ))(t('backend-caption-with-doc-link'));

    const objFieldProps = {
        webhook: {
            error: errors.uri,
            caption: skill.channel === Channel.AliceSkill ? aliceSkillWebhookDescription : smartHomeWebhookDescription,
        },
        function: {
            error: '',
            caption: ' ',
        },
    };

    const displayCloudFunctions =
        __appConfig.app.displayCloudFunctions || Boolean(window.sessionStorage.getItem('__display-cloud-functions'));

    const displayCloudFunctionsSmartHome =
        !getIsYangoRequest() &&
        (__appConfig.app.displayCloudFunctionsSmartHome ||
            Boolean(window.sessionStorage.getItem('__display-smart-home-cloud-functions')));

    const backendType = values.backendType;
    const fieldProps = objFieldProps[backendType];

    if (skill.channel === Channel.SmartHome && !displayCloudFunctionsSmartHome) {
        return (
            <Field required fieldTitle={l.uri} error={errors.uri} fieldDescription={smartHomeWebhookDescription}>
                <TextInput
                    className={styles.formElement}
                    value={values.uri}
                    onChange={(e) => onWebhookChange(e.target.value)}
                    type="url"
                    disabled={disabled}
                    size="xl"
                />
            </Field>
        );
    }

    if (skill.channel === Channel.AliceSkill && !displayCloudFunctions) {
        return (
            <Field required fieldTitle={l.uri} error={errors.uri} fieldDescription={aliceSkillWebhookDescription}>
                <TextInput
                    className={styles.formElement}
                    value={values.uri}
                    onChange={(e) => onWebhookChange(e.target.value)}
                    disabled={disabled}
                    type="url"
                    size="xl"
                />
            </Field>
        );
    }

    return (
        <div>
            <Field
                required
                fieldTitle="Backend"
                error={fieldProps.error as string}
                fieldDescription={fieldProps.caption}
            >
                <RadioButton
                    className={styles.backendTypeRadio}
                    name="backendType"
                    value={backendType}
                    onUpdate={onBackendTypeChange}
                    options={[
                        { value: 'webhook', content: l.uri },
                        { value: 'function', content: l.functionId },
                    ]}
                    size="xl"
                    disabled={disabled}
                />
                {backendType === 'webhook' ? (
                    <TextInput
                        className={styles.formElement}
                        value={values.uri}
                        onUpdate={onWebhookChange}
                        disabled={disabled}
                        type="url"
                        size="xl"
                    />
                ) : (
                    <div className={styles.functions}>
                        <FunctionsContainer
                            onFunctionIdChange={onFunctionIdChange}
                            disabled={disabled}
                            value={values.functionId}
                        />
                    </div>
                )}
            </Field>
        </div>
    );
}
